import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { scrollToId } from "@/components/SmoothScroll";
import { PersonalMessageButton } from "@/components/PersonalMessage";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, lang } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} id="top" className="relative h-screen min-h-[640px] w-full overflow-hidden" data-testid="hero">
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        {/* PREVIOUS HERO (forest) — keep for easy revert:
            src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400" */}
        {isDark ? (
          <img
            src="/hero-tree-bern-night.png"
            alt="Lone tree on a hill overlooking Bern, a dove in the sky"
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <img
              src="/hero-tree-bern-mobile.png"
              alt="Lone tree on a hill overlooking Bern, a dove in the sky"
              className="w-full h-full object-cover object-[60%_center] block sm:hidden"
            />
            <img
              src="/hero-tree-bern.png"
              alt="Lone tree on a hill overlooking Bern, a dove in the sky"
              className="w-full h-full object-cover hidden sm:block"
            />
          </>
        )}
        <div className={`absolute inset-0 ${isDark ? "bg-black/25" : "bg-emerald-950/15"}`} />
        <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? "from-black/85 via-black/25 to-transparent" : "from-[#12210b]/88 via-[#12210b]/30 to-transparent"}`} />
        <div className={`absolute inset-0 bg-gradient-to-r ${isDark ? "from-black/60 via-black/10 to-transparent" : "from-[#12210b]/65 via-[#12210b]/10 to-transparent"}`} />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-14 sm:pb-20 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="font-sans uppercase tracking-[0.25em] text-xs font-semibold text-white/90 mb-6 [text-shadow:0_1px_20px_rgba(0,0,0,0.5)]"
          data-testid="hero-overline"
        >
          {t("hero.overline")}
        </motion.p>

        <h1 className="font-serif text-white text-[2.25rem] leading-[1.05] sm:text-6xl sm:leading-[0.98] lg:text-8xl tracking-tight font-medium max-w-5xl [text-shadow:0_2px_50px_rgba(0,0,0,0.55)]">
          <Line delay={0.15}>{t("hero.line1")}</Line>
          <Line delay={0.25}>
            <em className="italic text-[#c9dca6]">{t("hero.line2")}</em>
          </Line>
          <Line delay={0.35}>{t("hero.line3")}</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-16"
        >
          <div className="max-w-md">
            <p className="font-sans text-white/85 text-base leading-relaxed font-light" data-testid="hero-sub">
              {t("hero.sub")}
            </p>
            <button
              onClick={() => scrollToId("activities")}
              data-testid="hero-cta-activities"
              className="mt-6 group rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-sans text-sm tracking-wide hover:bg-primary/90 transition-colors duration-300 flex items-center gap-2"
            >
              {t("hero.cta")}
              <ArrowDown size={16} strokeWidth={1.5} className="group-hover:translate-y-0.5 transition-transform duration-300" />
            </button>
          </div>
          <div className="max-w-xs">
            <p className="font-sans text-white/80 text-sm font-light leading-relaxed mb-3 [text-shadow:0_1px_12px_rgba(0,0,0,0.45)]" data-testid="hero-personal-text">
              {t("personal.text")}
            </p>
            <PersonalMessageButton className="rounded-full border border-white/40 text-white px-7 py-3.5 font-sans text-sm tracking-wide hover:bg-white/10 transition-colors duration-300 inline-flex items-center gap-2" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
